import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter } from 'rxjs';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { ClientVersionComponent } from './version/client-version-component/client-version-component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-root',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    MatToolbarModule,
    MatButtonModule,
    MatSidenavModule,
    MatIconModule,
    MatListModule,
    ClientVersionComponent,
    MatProgressSpinnerModule,
  ],
  standalone: true,
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  private readonly router = inject(Router);

  private readonly msal = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);

  protected readonly title = signal('Gartenwasserzähler - Admin');
  protected readonly loggedIn = signal(false);

  readonly showSpinner = signal(false);

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.showSpinner.set(true);
      }
      if (event instanceof NavigationEnd) {
        this.showSpinner.set(false);
      }
      if (event instanceof NavigationCancel) {
        this.showSpinner.set(false);
      }
      if (event instanceof NavigationError) {
        this.showSpinner.set(false);
      }
    });
  }

  ngOnInit() {
    console.log('App initialized, checking authentication status...');
    this.msal.handleRedirectObservable().subscribe();

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGOUT_SUCCESS ||
            msg.eventType === EventType.INITIALIZE_END,
        ),
      )
      .subscribe(() => {
        this.loggedIn.set(this.msal.instance.getAllAccounts().length > 0);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.loggedIn.set(this.msal.instance.getAllAccounts().length > 0);
        const active = this.msal.instance.getActiveAccount();
        if (!active) {
          const accounts = this.msal.instance.getAllAccounts();
          if (accounts.length > 0) {
            this.msal.instance.setActiveAccount(accounts[0]);
          } else {
            console.debug('No accounts found, user is not logged in');
          }
        }
      });
  }

  login() {
    this.msal.loginRedirect({ scopes: [] });
  }
  logout() {
    this.msal.logoutRedirect();
  }

  getAccountName(): string | undefined {
    const account = this.msal.instance.getActiveAccount();
    return account ? account.name : undefined;
  }
}
